import React, { useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, Film, CheckCircle, AlertCircle } from 'lucide-react';

export const FileUpload = ({
  accept = 'video/*',
  maxSize = 100 * 1024 * 1024, // 100MB
  maxDuration = 60,
  onChange,
  value,
  error,
  className = '',
  label = 'Video Clip',
  labelSuffix = '(Optional)',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileInfo, setFileInfo] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const validateVideo = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;

        if (duration > maxDuration) {
          reject(`Video is too long (${duration.toFixed(1)}s). Maximum: ${maxDuration}s`);
        } else {
          resolve({ duration, valid: true });
        }
      };

      video.onerror = () => {
        reject('Unable to read video file. Please try another file.');
      };

      video.src = URL.createObjectURL(file);
    });
  }, [maxDuration]);

  const handleFile = useCallback(async (file) => {
    setValidationError(null);

    if (!file) return;

    // Check file size
    if (file.size > maxSize) {
      setValidationError(`File is too large. Maximum size: ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    // Validate video duration
    try {
      const { duration } = await validateVideo(file);
      setFileInfo({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2),
        duration: duration.toFixed(1),
      });
      onChange?.(file);
    } catch (err) {
      setValidationError(err);
      setFileInfo(null);
      onChange?.(null);
    }
  }, [maxSize, validateVideo, onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const clearFile = useCallback(() => {
    setFileInfo(null);
    setValidationError(null);
    onChange?.(null);
  }, [onChange]);

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-medium text-zinc-300 mb-2">
        {label} <span className="text-zinc-500">{labelSuffix}</span>
      </label>
      
      <motion.div
        className={`
          relative
          border-2 border-dashed rounded-2xl
          transition-all duration-300
          ${isDragging 
            ? 'border-emerald-500 bg-emerald-500/10' 
            : fileInfo 
              ? 'border-emerald-500/50 bg-emerald-500/5' 
              : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/30'
          }
          ${validationError ? 'border-red-500/50 bg-red-500/5' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
      >
        <input
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />

        <AnimatePresence mode="wait">
          {fileInfo ? (
            <motion.div
              key="file-info"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-6"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Film className="w-6 h-6 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-100 font-medium truncate">{fileInfo.name}</p>
                  <p className="text-zinc-500 text-sm">
                    {fileInfo.size} MB • {fileInfo.duration}s duration
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearFile();
                    }}
                    className="p-2 rounded-lg hover:bg-zinc-800 transition-colors"
                  >
                    <X className="w-5 h-5 text-zinc-400" />
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="upload-prompt"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-8 text-center"
            >
              <div className={`
                w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center
                ${isDragging ? 'bg-emerald-500/20' : 'bg-zinc-800'}
                transition-colors
              `}>
                <Upload className={`w-8 h-8 ${isDragging ? 'text-emerald-400' : 'text-zinc-400'}`} />
              </div>
              <p className="text-zinc-100 font-medium mb-1">
                {isDragging ? 'Drop your video here' : 'Drag & drop your video clip'}
              </p>
              <p className="text-zinc-500 text-sm">
                or click to browse • MP4, WEBM, MOV • Max {maxDuration}s
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {(validationError || error) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-2 text-red-400 text-sm"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{validationError || error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-zinc-500 text-sm">
        Recommended: 20-60 seconds for optimal engagement
      </p>
    </div>
  );
};

export default FileUpload;
